// Recipe catalogue browse — the global, read-only pool every household
// publishes into. Anonymized (the server never sends author/household ids).
// Pick a recipe with "Add to our recipes" and it's copied into your
// household's editable box. Recipes you already have show "In your box".

import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { Icon, hueGrad } from "../../icons";
import { hueFromId } from "../../lib/use-recipes";
import { api, type CatalogueRecipe } from "../../lib/rpc";
import type { NotifyFn } from "../../components/toast";

interface RecipeCatalogueProps {
  open: boolean;
  onClose: () => void;
  notify: NotifyFn;
  /** Called after a successful import so the parent can refresh the box. */
  onImported: () => void;
}

export function RecipeCatalogue({ open, onClose, notify, onImported }: RecipeCatalogueProps) {
  const [search, setSearch] = useState("");
  const [list, setList] = useState<CatalogueRecipe[] | null>(null);
  const [importingId, setImportingId] = useState<number | null>(null);
  const reqRef = useRef(0);

  // Debounced fetch on open + as the search changes. A request token guards
  // against out-of-order responses landing after a newer query.
  useEffect(() => {
    if (!open) return;
    const token = ++reqRef.current;
    const t = setTimeout(() => {
      setList((prev) => (prev === null ? null : prev)); // keep showing prior while typing
      api.recipesCatalogue({ search }).then((rows) => {
        if (reqRef.current === token) setList(rows);
      }).catch((e: any) => {
        if (reqRef.current === token) { notify(e?.message || "couldn't load catalogue"); setList([]); }
      });
    }, search ? 220 : 0);
    return () => clearTimeout(t);
  }, [open, search]);

  // Reset transient state when the sheet closes so reopening is clean.
  useEffect(() => {
    if (open) return;
    setSearch("");
    setList(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const count = useMemo(() => list?.length ?? 0, [list]);

  if (!open) return null;

  const doImport = async (rec: CatalogueRecipe) => {
    if (rec.owned || importingId) return;
    setImportingId(rec.id);
    try {
      await api.recipesImportFromCatalogue(rec.id);
      setList((prev) => prev ? prev.map((x) => x.id === rec.id ? { ...x, owned: true } : x) : prev);
      notify(`Added "${rec.name}" to your recipes`);
      onImported();
    } catch (e: any) {
      notify(e?.message === "already_in_box" ? "Already in your recipes" : (e?.message || "couldn't add"));
    } finally {
      setImportingId(null);
    }
  };

  return (
    <div className="catalogue-bg" onClick={onClose}>
      <div className="catalogue-sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Recipe catalogue">
        <header className="catalogue-head">
          <div>
            <h2 className="catalogue-title">Recipe catalogue</h2>
            <p className="catalogue-sub">Everyone's published recipes. Add any to your household's box — then make it your own.</p>
          </div>
          <button className="icon-btn" onClick={onClose} title="Close" type="button">
            <Icon name="x" size={14} />
          </button>
        </header>

        <div className="catalogue-search">
          <Icon name="search" size={14} />
          <input
            placeholder="Search the catalogue… (name or #tag)"
            value={search}
            onInput={(e) => setSearch((e.currentTarget as HTMLInputElement).value)}
            autofocus
          />
        </div>

        <div className="catalogue-body">
          {list === null && <p className="list-picker-loading">Loading…</p>}
          {list !== null && count === 0 && (
            <div className="recipes-empty">
              <Icon name="flame" size={32} />
              <div className="recipes-empty-line">
                {search ? "No catalogue recipes match that search." : "The catalogue is empty."}
              </div>
            </div>
          )}
          {list !== null && count > 0 && (
            <div className="recipes-cards">
              {list.map((rec) => (
                <CatalogueCard
                  key={rec.id}
                  rec={rec}
                  importing={importingId === rec.id}
                  onAdd={() => doImport(rec)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CatalogueCard({ rec, importing, onAdd }: { rec: CatalogueRecipe; importing: boolean; onAdd: () => void }) {
  const stepCount = Array.isArray(rec.instructions) ? rec.instructions.length : 0;
  const tags = (rec.tags || []).slice(0, 3);
  return (
    <div className="recipe-card catalogue-card">
      <div
        className="recipe-card-cover"
        style={rec.imageUrl ? undefined : { background: hueGrad(hueFromId(String(rec.id))) }}
      >
        {rec.imageUrl && <img src={rec.imageUrl} alt={rec.name} />}
      </div>
      <div className="recipe-card-meta">
        <div className="recipe-card-title">{rec.name}</div>
        <div className="recipe-card-sub">
          {rec.itemCount ? `${rec.itemCount} ingredient${rec.itemCount === 1 ? "" : "s"}` : "no ingredients"}
          {stepCount > 0 ? ` · ${stepCount} step${stepCount === 1 ? "" : "s"}` : ""}
        </div>
        {tags.length > 0 && (
          <div className="recipe-card-tags">
            {tags.map((t) => <span key={t} className="recipe-card-tag">#{t}</span>)}
          </div>
        )}
      </div>
      {rec.owned ? (
        <div className="catalogue-owned"><Icon name="check" size={13} /><span>In your box</span></div>
      ) : (
        <button className="btn catalogue-add-btn" type="button" onClick={onAdd} disabled={importing}>
          <Icon name="plus" size={13} />
          <span>{importing ? "Adding…" : "Add to our recipes"}</span>
        </button>
      )}
    </div>
  );
}
